import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  MoreHorizontal,
  Pencil,
  RefreshCcw,
  TrendingUp,
  Undo2,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { Button } from '../components/ui/Button';
import { Drawer } from '../components/ui/Drawer';
import { TextAreaField } from '../components/ui/Field';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { formatDate, timeAgo } from '../lib/format';
import {
  AxisBreakdown,
  ModerationChip,
  SubmissionModerationDrawer,
  formatPeriodLabel,
  formatScore,
  type FeedbackAdminSubmission,
  type FeedbackAggregateRow,
  type FeedbackSubmissionDetailResponse,
  type FeedbackSubmissionAuditResponse,
  type FeedbackSubmissionAuditItem,
} from '../components/feedback';

export const Route = createFileRoute(
  '/_authed/hr/feedback/submissions/$submissionId',
)({
  component: SubmissionDetailPage,
});

// -------------------------------------------------------------------------
// Page
// -------------------------------------------------------------------------

function SubmissionDetailPage(): JSX.Element {
  const { submissionId } = Route.useParams();
  const hasRole = useAuthStore((s) => s.hasRole);
  const canManage = hasRole('super_admin', 'academic_admin');
  const isSuperAdmin = hasRole('super_admin');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const [moderateOpen, setModerateOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['feedback', 'submission', 'detail', submissionId] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackSubmissionDetailResponse>(
        `/api/feedback/admin/submissions/${encodeURIComponent(submissionId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const auditQuery = useQuery({
    queryKey: ['feedback', 'submission', 'audit', submissionId] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackSubmissionAuditResponse>(
        `/api/feedback/admin/submissions/${encodeURIComponent(submissionId)}/audit`,
        { signal },
      ),
    enabled: canManage,
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const detail = detailQuery.data;

  // Derive the parent edit-window state from the response. The handler
  // does not return this directly; the edit window is `submittedAt + form
  // .editWindowHours` (with `withdrawnAt` shutting the window early).
  const editWindow = useMemo(() => {
    if (!detail || !detail.form) {
      return { open: false, editableUntil: null as string | null };
    }
    const editWindowMs = detail.form.editWindowHours * 60 * 60 * 1000;
    const submittedMs = Date.parse(detail.submission.submittedAt);
    if (!Number.isFinite(submittedMs)) {
      return { open: false, editableUntil: null };
    }
    const until = submittedMs + editWindowMs;
    const editableUntil = new Date(until).toISOString();
    if (detail.submission.withdrawnAt) {
      return { open: false, editableUntil };
    }
    return { open: until > Date.now(), editableUntil };
  }, [detail]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ['feedback', 'submission', 'detail', submissionId],
    });
    void queryClient.invalidateQueries({
      queryKey: ['feedback', 'submission', 'audit', submissionId],
    });
    void queryClient.invalidateQueries({ queryKey: ['feedback', 'admin'] });
  };

  const recomputeMutation = useMutation({
    mutationFn: () =>
      apiRequest<{
        ok: true;
        scanned: number;
        recomputed: number;
        errors: number;
      }>('/api/feedback/admin/recompute', {
        method: 'POST',
        body: detail
          ? {
              periodKey: detail.submission.periodKey,
              teacherId: detail.submission.teacherId,
            }
          : {},
      }),
    onSuccess: (res) => {
      toast(
        `Recomputed ${res.recomputed} teacher${res.recomputed === 1 ? '' : 's'}${
          res.errors > 0
            ? ` · ${res.errors} error${res.errors === 1 ? '' : 's'}`
            : ''
        }.`,
        { variant: res.errors > 0 ? 'info' : 'success' },
      );
      invalidate();
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not recompute.',
        { variant: 'error' },
      ),
  });

  const withdrawMutation = useMutation({
    mutationFn: (note: string) =>
      apiRequest<{ ok: true }>(
        `/api/feedback/admin/submissions/${encodeURIComponent(submissionId)}/withdraw`,
        { method: 'POST', body: { note } },
      ),
    onSuccess: () => {
      toast('Submission withdrawn on parent behalf.', { variant: 'success' });
      setWithdrawOpen(false);
      invalidate();
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not withdraw.',
        { variant: 'error' },
      ),
  });

  const handleRecompute = async (): Promise<void> => {
    setOverflowOpen(false);
    const ok = await confirm({
      title: 'Recompute affected aggregate?',
      description:
        'Rebuilds the teacher aggregate for the period this submission belongs to. Hidden or withdrawn rows are excluded by the next run.',
      confirmLabel: 'Recompute',
    });
    if (!ok) return;
    recomputeMutation.mutate();
  };

  const moderationHistoryCount = auditQuery.data?.items.length ?? null;

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!detail) return [];
    const responses = detail.submission.responses;
    const overall =
      typeof responses?.overall === 'number' ? responses.overall : null;
    const matchingOverallX100 =
      detail.matchingAggregate?.overallAvgX100 ?? null;
    return [
      {
        label: 'Overall score',
        value: overall === null ? '—' : `${overall.toFixed(1)} / 5`,
      },
      {
        label: 'Period responses',
        value: detail.matchingAggregate
          ? detail.matchingAggregate.responsesCount
          : '—',
        sub: detail.matchingAggregate
          ? formatPeriodLabel(detail.matchingAggregate.periodKey)
          : undefined,
      },
      {
        // The backend returns the aggregate row matching this submission's
        // (form, period, teacher, batch) tuple — when the submission carries
        // a batchId, the score is batch-scoped, not the teacher's cross-batch
        // overall. We label it honestly ("Slot overall") to avoid implying
        // a teacher-wide figure the payload doesn't provide.
        label: 'Slot overall',
        value:
          matchingOverallX100 !== null
            ? formatScore(matchingOverallX100)
            : '—',
        sub: detail.matchingAggregate?.batchId
          ? 'this batch · this period'
          : 'this period',
      },
      {
        label: 'Edit window',
        value: editWindow.open ? 'Open' : 'Closed',
        sub: editWindow.editableUntil
          ? editWindow.open
            ? `until ${formatDate(editWindow.editableUntil, { withTime: true })}`
            : `closed ${timeAgo(editWindow.editableUntil)}`
          : undefined,
      },
      {
        label: 'Moderation events',
        value: moderationHistoryCount ?? '—',
      },
    ];
  }, [detail, editWindow, moderationHistoryCount]);

  // Synthesise a `FeedbackAdminSubmission` shape from the detail response so
  // the existing moderation drawer (which takes the list-row type) can render
  // without a second fetch. The list type is intentionally a subset of the
  // detail submission shape minus the anti-gaming + form fields.
  const drawerSubmission = useMemo<FeedbackAdminSubmission | null>(() => {
    if (!detail) return null;
    return {
      id: detail.submission.id,
      formId: detail.submission.formId,
      periodKey: detail.submission.periodKey,
      teacherId: detail.submission.teacherId,
      teacherName: detail.teacher?.name ?? null,
      batchId: detail.submission.batchId,
      batchName: detail.batch?.name ?? null,
      householdId: detail.submission.householdId,
      studentId: detail.submission.studentId,
      studentName: detail.student?.fullName ?? null,
      responses: detail.submission.responses,
      comment: detail.submission.comment,
      moderationStatus: detail.submission.moderationStatus,
      submittedAt: detail.submission.submittedAt,
      editedAt: detail.submission.editedAt,
      withdrawnAt: detail.submission.withdrawnAt,
    };
  }, [detail]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={detailQuery.error}
        title="Feedback submission"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !detail ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={`${detail.student?.fullName ?? 'Student'} → ${detail.teacher?.name ?? 'Teacher'}`}
              sublabel={
                <>
                  <span>{formatPeriodLabel(detail.submission.periodKey)}</span>
                  <span aria-hidden="true">·</span>
                  <span>
                    submitted {timeAgo(detail.submission.submittedAt)}
                  </span>
                  {detail.submitter ? (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>by {detail.submitter.displayName}</span>
                    </>
                  ) : null}
                </>
              }
              statusChip={
                <ModerationChip status={detail.submission.moderationStatus} />
              }
              backTo="/hr/feedback"
              actions={
                <>
                  {canManage ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setModerateOpen(true)}
                    >
                      Moderate
                    </Button>
                  ) : null}
                  {detail.student ? (
                    <Link
                      to="/people/students/$studentId"
                      params={{ studentId: detail.student.id }}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 text-xs font-medium text-[color:var(--color-text)] hover:border-gold-500"
                    >
                      <ExternalLink size={12} aria-hidden="true" />
                      Student
                    </Link>
                  ) : null}
                  {detail.teacher ? (
                    <Link
                      to="/hr/feedback/teachers/$teacherId"
                      params={{ teacherId: detail.teacher.id }}
                      search={{ periodKey: detail.submission.periodKey }}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 text-xs font-medium text-[color:var(--color-text)] hover:border-gold-500"
                    >
                      <TrendingUp size={12} aria-hidden="true" />
                      Aggregates
                    </Link>
                  ) : null}
                  {canManage ? (
                    <OverflowMenu
                      open={overflowOpen}
                      onOpenChange={setOverflowOpen}
                      items={[
                        {
                          label: 'Recompute affected aggregate',
                          icon: <RefreshCcw size={14} aria-hidden="true" />,
                          onSelect: () => {
                            void handleRecompute();
                          },
                          disabled: recomputeMutation.isPending,
                        },
                        ...(isSuperAdmin && !detail.submission.withdrawnAt
                          ? [
                              {
                                label: "Withdraw on parent's behalf",
                                icon: <Undo2 size={14} aria-hidden="true" />,
                                onSelect: () => {
                                  setOverflowOpen(false);
                                  setWithdrawOpen(true);
                                },
                                danger: true,
                              },
                            ]
                          : []),
                      ]}
                    />
                  ) : null}
                </>
              }
            />

            <KpiStrip items={kpiItems} />

            <ResponseBreakdownCard detail={detail} />

            <div className="grid gap-4 lg:grid-cols-2">
              <ParentChildCard detail={detail} />
              <TeacherContextCard detail={detail} />
            </div>

            <AntiGamingCard detail={detail} />

            <ModerationHistoryCard
              query={auditQuery}
              canView={canManage}
            />

            <ActivityPanel
              targetType="feedback_submission"
              targetId={detail.submission.id}
              title="Activity"
            />
          </div>
        )}
      </GatedSection>

      <SubmissionModerationDrawer
        submission={moderateOpen ? drawerSubmission : null}
        canManage={canManage}
        onClose={() => setModerateOpen(false)}
        onModerated={() => {
          setModerateOpen(false);
          invalidate();
        }}
      />

      {detail && isSuperAdmin ? (
        <WithdrawDrawer
          open={withdrawOpen}
          studentName={detail.student?.fullName ?? 'this submission'}
          teacherName={detail.teacher?.name ?? 'the teacher'}
          onClose={() => setWithdrawOpen(false)}
          onSubmit={(note) => withdrawMutation.mutate(note)}
          pending={withdrawMutation.isPending}
        />
      ) : null}
    </div>
  );
}

// -------------------------------------------------------------------------
// Sections
// -------------------------------------------------------------------------

function ResponseBreakdownCard({
  detail,
}: {
  detail: FeedbackSubmissionDetailResponse;
}): JSX.Element {
  const responses = detail.submission.responses;
  const synthRow = useMemo<FeedbackAggregateRow | null>(() => {
    if (!responses) return null;
    return {
      id: detail.submission.id,
      formId: detail.submission.formId,
      periodKey: detail.submission.periodKey,
      teacherId: detail.submission.teacherId,
      batchId: detail.submission.batchId,
      responsesCount: 1,
      clarityAvgX100: responses.clarity * 100,
      patienceAvgX100: responses.patience * 100,
      communicationAvgX100: responses.communication * 100,
      progressAvgX100: responses.progress * 100,
      overallAvgX100: responses.overall * 100,
      weightedScoreX100: responses.overall * 100,
      visibleToTeacher: false,
      lastRecomputedAt: detail.submission.submittedAt,
    };
  }, [responses, detail.submission]);

  return (
    <SectionCard title="Response breakdown">
      {synthRow ? (
        <AxisBreakdown aggregate={synthRow} />
      ) : (
        <p className="text-sm text-[color:var(--color-text-muted)]">
          No response payload recorded.
        </p>
      )}
      {detail.submission.comment ? (
        <div className="mt-4 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-3 text-sm whitespace-pre-wrap">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
            Comment
          </p>
          {detail.submission.comment}
        </div>
      ) : null}
    </SectionCard>
  );
}

function ParentChildCard({
  detail,
}: {
  detail: FeedbackSubmissionDetailResponse;
}): JSX.Element {
  return (
    <SectionCard title="Parent & child">
      <Field label="Child" value={detail.student?.fullName ?? '—'} />
      <Field
        label="Submitted by"
        value={detail.submitter?.displayName ?? '—'}
      />
      {detail.submitter?.email ? (
        <Field label="Email" value={detail.submitter.email} mono />
      ) : null}
      <Field
        label="Family"
        value={detail.household?.primaryContactName ?? '—'}
      />
      {detail.household?.primaryEmail ? (
        <Field label="Family email" value={detail.household.primaryEmail} mono />
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {detail.student ? (
          <Link
            to="/people/students/$studentId"
            params={{ studentId: detail.student.id }}
            className="text-xs text-gold-700 underline hover:no-underline"
          >
            Open student profile
          </Link>
        ) : null}
        {detail.household ? (
          <Link
            to="/people/households/$householdId"
            params={{ householdId: detail.household.id }}
            className="text-xs text-gold-700 underline hover:no-underline"
          >
            Open family
          </Link>
        ) : null}
      </div>
    </SectionCard>
  );
}

function TeacherContextCard({
  detail,
}: {
  detail: FeedbackSubmissionDetailResponse;
}): JSX.Element {
  return (
    <SectionCard title="Teacher context">
      <Field label="Teacher" value={detail.teacher?.name ?? '—'} />
      <Field label="Batch" value={detail.batch?.name ?? '—'} />
      <Field label="Form" value={detail.form?.title ?? '—'} />
      <Field
        label="Cadence"
        value={
          detail.form
            ? `${detail.form.scope} · ${detail.form.cadence}`
            : '—'
        }
      />
      <div className="mt-3 flex flex-wrap gap-2">
        {detail.teacher ? (
          <Link
            to="/hr/feedback/teachers/$teacherId"
            params={{ teacherId: detail.teacher.id }}
            search={{ periodKey: detail.submission.periodKey }}
            className="text-xs text-gold-700 underline hover:no-underline"
          >
            Open aggregates
          </Link>
        ) : null}
        {detail.form ? (
          <Link
            to="/hr/feedback/forms/$formId"
            params={{ formId: detail.form.id }}
            className="text-xs text-gold-700 underline hover:no-underline"
          >
            Open form
          </Link>
        ) : null}
      </div>
    </SectionCard>
  );
}

function AntiGamingCard({
  detail,
}: {
  detail: FeedbackSubmissionDetailResponse;
}): JSX.Element {
  return (
    <SectionCard title="Anti-gaming signals">
      <Field
        label="Source"
        value={detail.submission.source ?? 'portal'}
      />
      <Field
        label="IP hash"
        value={detail.submission.ipHash ?? '—'}
        mono
      />
      <Field
        label="User-agent hash"
        value={detail.submission.uaHash ?? '—'}
        mono
      />
      <Field
        label="Anonymous to teacher"
        value={detail.submission.isAnonymousToTeacher ? 'Yes' : 'No'}
      />
      <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
        Hashes are SHA-256 of the requester IP and user agent. They let HR
        correlate submissions across a family without storing PII.
      </p>
    </SectionCard>
  );
}

interface ModerationHistoryCardProps {
  query: ReturnType<
    typeof useQuery<FeedbackSubmissionAuditResponse, unknown>
  >;
  canView: boolean;
}

function ModerationHistoryCard({
  query,
  canView,
}: ModerationHistoryCardProps): JSX.Element {
  if (!canView) {
    return (
      <RelationPanel
        title="Moderation history"
        isEmpty
        empty={{
          copy: 'You do not have permission to view the moderation history.',
        }}
      >
        <div />
      </RelationPanel>
    );
  }

  const items = query.data?.items ?? [];
  const isEmpty = !query.isLoading && items.length === 0;

  const errorState =
    query.isError && !query.data
      ? {
          message:
            query.error instanceof ApiError
              ? query.error.message
              : 'Could not load moderation history.',
          onRetry: () => {
            void query.refetch();
          },
        }
      : null;

  return (
    <RelationPanel
      title="Moderation history"
      count={items.length}
      loading={query.isLoading && !query.data}
      error={errorState}
      empty={{ copy: 'No moderation events recorded.' }}
      isEmpty={isEmpty}
    >
      <ol className="space-y-2">
        {items.map((item) => (
          <ModerationRow key={item.id} item={item} />
        ))}
      </ol>
    </RelationPanel>
  );
}

function ModerationRow({
  item,
}: {
  item: FeedbackSubmissionAuditItem;
}): JSX.Element {
  const actor =
    item.actorDisplayName?.trim() ||
    item.actorEmail?.trim() ||
    'system';
  const noteValue = item.metadata?.['note'];
  const note = typeof noteValue === 'string' ? noteValue : null;
  return (
    <li className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-3 py-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-[color:var(--color-text)]">
            <span className="font-medium">{actor}</span>
            <span className="mx-1.5 text-[color:var(--color-text-muted)]">·</span>
            <span>{formatActionLabel(item.action)}</span>
          </p>
          {item.actorRole ? (
            <p className="text-[11px] capitalize text-[color:var(--color-text-muted)]">
              {item.actorRole.replace(/_/g, ' ')}
            </p>
          ) : null}
        </div>
        <time
          dateTime={item.occurredAt}
          title={formatDate(item.occurredAt, { withTime: true })}
          className="shrink-0 text-xs tabular-nums text-[color:var(--color-text-muted)]"
        >
          {timeAgo(item.occurredAt)}
        </time>
      </div>
      {note ? (
        <p className="mt-2 text-xs text-[color:var(--color-text)] whitespace-pre-wrap">
          {note}
        </p>
      ) : null}
    </li>
  );
}

const formatActionLabel = (action: string): string => {
  const segments = action.split('.');
  const last = segments[segments.length - 1] ?? action;
  return last
    .split(/[_-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

// -------------------------------------------------------------------------
// Withdraw drawer
// -------------------------------------------------------------------------

interface WithdrawDrawerProps {
  open: boolean;
  studentName: string;
  teacherName: string;
  onClose: () => void;
  onSubmit: (note: string) => void;
  pending: boolean;
}

function WithdrawDrawer({
  open,
  studentName,
  teacherName,
  onClose,
  onSubmit,
  pending,
}: WithdrawDrawerProps): JSX.Element {
  const [note, setNote] = useState('');

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="md"
      title="Withdraw on parent's behalf"
    >
      <div className="space-y-4">
        <p className="text-sm text-[color:var(--color-text-muted)]">
          Marks this submission as withdrawn outside the parent edit window.
          The aggregator will exclude it on the next recompute. The note
          becomes part of the audit trail.
        </p>
        <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3 text-sm">
          <p className="font-medium">{studentName}</p>
          <p className="text-xs text-[color:var(--color-text-muted)]">
            feedback for {teacherName}
          </p>
        </div>
        <TextAreaField
          label="Audit note"
          hint="A short explanation visible in the audit trail (required)."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            loading={pending}
            disabled={note.trim().length < 1}
            onClick={() => onSubmit(note.trim())}
          >
            Withdraw submission
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

// -------------------------------------------------------------------------
// Overflow menu + section primitives
// -------------------------------------------------------------------------

interface OverflowItem {
  label: string;
  icon?: JSX.Element;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
}

interface OverflowMenuProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  items: OverflowItem[];
}

function OverflowMenu({
  open,
  onOpenChange,
  items,
}: OverflowMenuProps): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        icon={<MoreHorizontal size={14} aria-hidden="true" />}
      >
        More
      </Button>
      {open ? (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => onOpenChange(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <ul
            role="menu"
            className="absolute right-0 z-20 mt-1 w-64 overflow-hidden rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] shadow-lg"
          >
            {items.map((item) => (
              <li key={item.label} role="none">
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={item.onSelect}
                  className={clsx(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                    'hover:bg-[color:var(--color-surface)]',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    item.danger && 'text-[color:var(--color-danger)]',
                  )}
                >
                  {item.icon}
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {title}
      </h3>
      <div className="mt-3 space-y-2 text-sm">{children}</div>
    </section>
  );
}

interface FieldProps {
  label: string;
  value: string;
  mono?: boolean;
}

function Field({ label, value, mono = false }: FieldProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <span className="text-xs text-[color:var(--color-text-muted)]">
        {label}
      </span>
      <span
        className={clsx(
          'min-w-0 text-right text-[color:var(--color-text)]',
          mono && 'font-mono text-xs break-all',
        )}
      >
        {value}
      </span>
    </div>
  );
}
